import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useIntercom } from "react-use-intercom";

import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";

import { useAuth } from "common/context/AuthContext";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import { getIntercomPlanTypeLabel } from "common/helpers/intercomWorkspacePlan";
import useCello from "common/hooks/useCello";

export default function useWorkspaceChangeUpdates() {
  const { isAuthorized } = useAuth();
  const { isReady: isCelloReady } = useCello(isAuthorized);
  const workspace = useSelectedWorkspaceContext();

  const {
    accounts,
    id: workspaceId,
    lifetime_license_count: workspaceLtdCount,
    subscription,
    rental_subscriptions: rentalSubscriptions,
    agency_license_count: agencyLicenseCount,
  } = workspace ?? {};

  const { subscriptionPaymentSource } = useSubscriptionPaymentSource(
    subscription?.id,
    false,
  );
  const { agencyPlan, ltdPromotion, appSumoPromotion } = usePromotions();

  const hasActiveAgencyPlan = Boolean(
    agencyPlan?.subscription?.status === "active",
  );

  const { update } = useIntercom();

  const totalLtdCount =
    (ltdPromotion?.license_count ?? 0) + (appSumoPromotion?.license_count ?? 0);

  useEffect(() => {
    // Intercom update() is a no-op until boot() runs (react-use-intercom). Boot in
    // useAppInit waits for Cello readiness; without isCelloReady in deps, the first
    // workspace update often runs before boot and never runs again for the same workspace.
    if (!workspace || !isCelloReady) {
      return;
    }

    let paymentMethod = "No payment method";
    if (subscriptionPaymentSource) {
      paymentMethod =
        subscriptionPaymentSource.type === "card"
          ? `Card ending in ${subscriptionPaymentSource.card.last4}`
          : `Paypal email ${subscriptionPaymentSource.paypal.email}`;
    }

    const loggedOutAccountsCount = accounts.filter(
      (account) => account.state !== "LoggedIn",
    ).length;

    const loggedInAccountsCount = accounts.filter(
      (account) => account.state === "LoggedIn",
    ).length;

    update({
      customAttributes: {
        "Workspace ID": workspaceId,
        "Payment Method": paymentMethod,
        "LoggedOut accounts count": loggedOutAccountsCount,
        "LoggedIn accounts count": loggedInAccountsCount,
        "Workspace LTD Count": workspaceLtdCount,
        "Total LTD Count": totalLtdCount,
        "Plan Type": getIntercomPlanTypeLabel(workspace, agencyPlan),
        "Agency client": hasActiveAgencyPlan,
        ...(subscription && {
          "Subscription ID": subscription.id,
          "Subscription Status": subscription.status,
          "Next Billing Date": new Date(
            subscription.next_billing * 1000,
          ).toISOString(),
        }),
      },
    });
    Sentry.setTag("workspace id", workspaceId);
  }, [
    isCelloReady,
    workspace,
    workspaceId,
    subscriptionPaymentSource,
    subscription,
    rentalSubscriptions,
    agencyLicenseCount,
    workspaceLtdCount,
    accounts,
    totalLtdCount,
    hasActiveAgencyPlan,
    agencyPlan,
  ]);
}
